/** TanStack Query hooks for search and autocomplete suggestions */

import { useMutation, useQuery } from '@tanstack/react-query'
import { apiClient } from '../lib/api-client'
import type {
  SearchClickEvent,
  SearchFilters,
  SearchResponse,
  SuggestResponse,
} from '@agentwiki/shared'
import { searchDocumentsSchema } from '@agentwiki/shared'
import { getWebFeatureProfile } from '../lib/feature-flags'

export type SearchSource = 'docs' | 'storage' | 'all'
export type SearchType = 'hybrid' | 'keyword' | 'semantic'

/** Category, tag, and date filters describe documents, not storage uploads. */
export function searchSourceSupportsDocumentFilters(source: SearchSource): boolean {
  return source === 'docs'
}

export interface SearchQueryOptions {
  source?: SearchSource
  type?: SearchType
  limit?: number
  filters?: SearchFilters
  facets?: boolean
  debug?: boolean
}

type LegacySearchArg = SearchSource | SearchQueryOptions

function normalizeSearchOptions(
  options?: LegacySearchArg,
): Required<Omit<SearchQueryOptions, 'filters' | 'debug'>> &
  Pick<SearchQueryOptions, 'filters' | 'debug'> {
  const storageEnabled = getWebFeatureProfile().storageSpace
  if (typeof options === 'string') {
    return {
      source: storageEnabled ? options : 'docs',
      type: 'hybrid',
      limit: 10,
      facets: false,
      filters: undefined,
      debug: undefined,
    }
  }

  return {
    source: storageEnabled ? (options?.source ?? 'all') : 'docs',
    type: options?.type ?? 'hybrid',
    limit: options?.limit ?? 10,
    facets: options?.facets ?? false,
    filters: options?.filters,
    debug: options?.debug,
  }
}

/** Build the API search path from workspace state. Exported for focused tests. */
export function buildSearchPath(query: string, options?: LegacySearchArg): string {
  const normalized = normalizeSearchOptions(options)
  const filters = searchSourceSupportsDocumentFilters(normalized.source)
    ? normalized.filters
    : undefined
  const bounded = searchDocumentsSchema.safeParse({
    query,
    category: filters?.category,
    tags: filters?.tags,
    dateFrom: filters?.dateFrom,
    dateTo: filters?.dateTo,
    limit: normalized.limit,
    offset: 0,
  })
  if (!bounded.success) throw new Error('Search input exceeds the shared bounds')
  const params = new URLSearchParams({
    q: bounded.data.query,
    type: normalized.type,
    limit: String(normalized.limit),
    source: normalized.source,
  })

  if (normalized.facets) params.set('facets', 'true')
  if (normalized.debug) params.set('debug', 'true')
  if (searchSourceSupportsDocumentFilters(normalized.source)) {
    if (bounded.data.category) params.set('category', bounded.data.category)
    if (bounded.data.dateFrom) params.set('dateFrom', bounded.data.dateFrom)
    if (bounded.data.dateTo) params.set('dateTo', bounded.data.dateTo)
    for (const tag of bounded.data.tags ?? []) {
      params.append('tags[]', tag)
    }
  }

  // Keep `%20` space encoding for existing Playwright response matchers and
  // historical request shape from the command palette.
  return `/api/search?${params.toString().replace(/\+/g, '%20')}`
}

/** Hybrid search hook — triggers for queries >= 2 chars */
export function useSearch(query: string, options?: LegacySearchArg) {
  const normalized = normalizeSearchOptions(options)
  return useQuery<SearchResponse>({
    queryKey: ['search', query, normalized],
    queryFn: () => apiClient.get<SearchResponse>(buildSearchPath(query, normalized)),
    enabled: query.length >= 2,
    staleTime: 30_000,
    placeholderData: (prev) => prev, // keep previous results while fetching new query
  })
}

/** Autocomplete suggestions hook — triggers for queries >= 1 char */
export function useSuggest(query: string) {
  return useQuery<SuggestResponse>({
    queryKey: ['suggest', query],
    queryFn: () =>
      apiClient.get<SuggestResponse>(`/api/search/suggest?q=${encodeURIComponent(query)}&limit=7`),
    enabled: query.length >= 1,
    staleTime: 60_000,
    placeholderData: (prev) => prev, // keep previous suggestions while fetching
  })
}

/** Fire-and-forget search click tracking for document results. */
export function useRecordSearchClick() {
  return useMutation({
    mutationFn: (event: SearchClickEvent) =>
      apiClient.post<{ ok: true }>('/api/search/track', event),
  })
}
